import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BodyCard from "../../../../components/fundamentals/body-card/body-card";
import TableViewHeader from "../../../../components/fundamentals/custom-table-header";
import Spinner from "../../../../components/fundamentals/spinner";
import dashboardSyncService, { HealthReport, SyncDashboard } from "../../../../services/dashboard-sync.service";

// RAG-67 : noms lisibles des connecteurs tiers renvoyés par /health/check (champ `services`).
// `db` est exclu : il relève du statut critique « plateforme », pas des connecteurs.
const CONNECTOR_LABELS: Record<string, string> = {
  bucket: "Stockage",
  esi: "ESI",
  pim: "PIM Socoda",
  pim_interactiv: "PIM Interactiv",
  plateforme_socoda: "Plateforme Socoda",
  sineres: "ERP Sinérès",
  strapi: "Strapi (contenus)",
};

interface CardProps {
  title: string;
  value: React.ReactNode;
  hint?: string;
  onClick?: () => void;
  alert?: boolean;
}

function IndicatorCard({ title, value, hint, onClick, alert }: CardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "flex flex-col items-start text-left p-base rounded-rounded border transition-colors " +
        (alert ? "border-rose-30 bg-rose-10" : "border-grey-20 bg-grey-0") +
        (onClick ? " hover:border-blue-60 cursor-pointer" : " cursor-default")
      }
    >
      <span className="inter-small-regular text-grey-50">{title}</span>
      <span className={"inter-xlarge-semibold mt-1 " + (alert ? "text-rose-50" : "text-grey-90")}>{value}</span>
      {hint && <span className="inter-small-regular text-grey-40 mt-1">{hint}</span>}
    </button>
  );
}

/**
 * RAG-40 — Cartes d'indicateurs de synchronisation et de santé des connecteurs.
 * Chaque carte renvoie vers l'écran détaillé correspondant.
 */
export default function SyncIndicators() {
  const navigate = useNavigate();
  const [data, setData] = useState<SyncDashboard | null>(null);
  const [health, setHealth] = useState<HealthReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Les indicateurs (base) pilotent l'affichage ; la santé des connecteurs (probe
    // live, potentiellement lente) se remplit à part pour ne pas bloquer les cartes.
    dashboardSyncService.getSync().then((sync) => {
      setData(sync);
      setLoading(false);
    });
    dashboardSyncService.getHealth().then((report) => setHealth(report));
  }, []);

  const lastSync = data?.last_syncs?.[0];

  // RAG-67 : santé réelle des connecteurs (champ `services`). On nomme ceux qui sont
  // EN PANNE (valeur strictement false) ; un connecteur « non configuré » (null, ex.
  // Plateforme Socoda en sommeil) n'est PAS une panne et ne doit pas s'afficher en rouge.
  // `status` (strict : base + connecteurs branchés) sert de filet pour un souci critique
  // sans connecteur nommé (base de données / disque).
  const services = health?.services ?? {};
  const downConnectors = Object.keys(services)
    .filter((key) => key !== "db" && services[key] === false)
    .map((key) => CONNECTOR_LABELS[key] ?? key);
  const platformKo = health ? !health.status : false;
  const connectorsValue =
    health === null
      ? "?"
      : downConnectors.length > 0
      ? `${downConnectors.length} en problème`
      : platformKo
      ? "Problème"
      : "OK";
  const connectorsHint =
    health === null
      ? "Sonde indisponible"
      : downConnectors.length > 0
      ? downConnectors.join(" · ")
      : platformKo
      ? "Base de données ou disque"
      : "Tous les connecteurs répondent";

  return (
    <BodyCard forceDropdown={false} customHeader={<TableViewHeader views={["Synchronisation"]} />} className="h-fit">
      {loading ? (
        <div className="py-large flex justify-center">
          <Spinner />
        </div>
      ) : !data ? (
        <p className="py-base text-grey-50 inter-small-regular">Indicateurs de synchronisation indisponibles.</p>
      ) : (
        <div className="grid grid-cols-2 large:grid-cols-4 gap-4 mt-base">
          <IndicatorCard
            title="Produits par source"
            value={`${data.products_by_source.socoda} Socoda`}
            hint={`${data.products_by_source.interactiv} Interactiv · ${data.products_by_source.plateforme} Plateforme · ${data.products_by_source.csv} CSV`}
            onClick={() => navigate("/products")}
          />
          <IndicatorCard
            title="Fournisseurs sans PIM attribué"
            value={data.suppliers_without_rule}
            hint="Source « auto » par défaut"
            onClick={() => navigate("/suppliers")}
          />
          <IndicatorCard
            title="Correspondances catégories en attente"
            value={data.pending_category_mappings}
            alert={data.pending_category_mappings > 0}
            onClick={() => navigate("/category-mappings")}
          />
          <IndicatorCard
            title="Correspondances marques en attente"
            value={data.pending_brand_mappings}
            alert={data.pending_brand_mappings > 0}
            onClick={() => navigate("/brand-mappings")}
          />
          <IndicatorCard
            title="Erreurs de synchro (24 h)"
            value={data.sync_errors_24h}
            alert={data.sync_errors_24h > 0}
            onClick={() => navigate("/settings/synchronisation")}
          />
          <IndicatorCard
            title="Dernière synchro"
            value={lastSync ? lastSync.status : "—"}
            hint={lastSync ? `${lastSync.name}${lastSync.ended_at ? " · " + lastSync.ended_at : ""}` : "Aucune"}
            alert={lastSync?.status === "fail"}
            onClick={() => navigate("/settings/synchronisation")}
          />
          <IndicatorCard
            title="Connecteurs (santé)"
            value={connectorsValue}
            hint={connectorsHint}
            alert={health !== null && (platformKo || downConnectors.length > 0)}
          />
        </div>
      )}
    </BodyCard>
  );
}
